// @ts-strict-ignore
import type { ValidityChangeCallback } from '@backend/lib-design-system';
import { DownFilledIcon } from '@backend/lib-design-system/icons';
import { type ChangeEvent, type FocusEventHandler, type KeyboardEventHandler, useEffect, useState } from 'react';

import { ClearIcon, Container, StyledControl, StyledOption } from './Select.style';

export interface SelectOption {
  value: string;
  label?: string;
}

type SelectProps = {
  required?: boolean;
  value: string | null;
  onChange?: (value: string | null) => void;
  search?: string | null;
  onSearchChange?: (search: string | null) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onBeforeInput?: () => void;
  placeholder?: string;
  options: SelectOption[];
  formatLabel?: (value: string) => string;
  onValidityChange?: ValidityChangeCallback;
  onFocus?: ((e: FocusEvent) => void) | undefined;
  onInput?: ((e: ChangeEvent<HTMLInputElement>) => void) | undefined;
  fontSize?: 'l' | 'm';
  clearable?: boolean;
  disableEnterSelection?: boolean;
};

export const Select = ({
  required,
  value,
  search,
  options,
  placeholder,
  onSearchChange,
  onChange,
  onBlur,
  onBeforeInput,
  formatLabel,
  onValidityChange,
  onFocus,
  onInput,
  fontSize = 'm',
  clearable,
  disableEnterSelection,
}: SelectProps) => {
  const [isDropdownOpened, setDropdownOpened] = useState(false);

  useEffect(() => {
    if (!onValidityChange) {
      return;
    }
    if (required && !value?.trim()) {
      onValidityChange({ isValid: false, reason: 'valueMissing' });
    } else {
      onValidityChange({ isValid: true, reason: undefined });
    }
  }, [value, required, onValidityChange]);

  const handleClearIconClick = () => {
    onChange(null);
  };

  return (
    <Container isOpened={isDropdownOpened && Boolean(options.length)}>
      <StyledControl
        value={value}
        onBlur={onBlur}
        search={search}
        onInput={onInput}
        onFocus={onFocus}
        onChange={onChange as (value: string | Record<string, any> | null) => void}
        required={required}
        fontSize={fontSize}
        $clearable={clearable}
        placeholder={placeholder}
        formatLabel={formatLabel as (value: string | Record<string, any>) => string}
        isOpened={isDropdownOpened}
        onBeforeInput={onBeforeInput}
        onSearchChange={onSearchChange}
        disableEnterSelection={disableEnterSelection}
        onOpenedChange={setDropdownOpened}
        suffix={
          <>
            {clearable && value && <ClearIcon size="l" onClick={handleClearIconClick} />}
            <DownFilledIcon size="m" />
          </>
        }
      >
        {options.map(({ value: optionValue, label }) => (
          <StyledOption key={`option_key_${optionValue}`} value={optionValue} label={label} />
        ))}
      </StyledControl>
    </Container>
  );
};
